import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Lead } from '../api/types';
import { apiGet, apiPost } from '../api/client';
import { stageLabel, timeAgo, pad2 } from '../lib/format';
import styles from './LibraryPage.module.css';

export default function LibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get('q') ?? '';
  const stageFilter = searchParams.get('stage') ?? '';
  const tierFilter = searchParams.get('tier') ?? '';

  const [status, setStatus] = useState<{
    loading: boolean;
    error: string | null;
    message: { text: string; type: 'success' | 'error' } | null;
  }>({ loading: true, error: null, message: null });
  const [leads, setLeads] = useState<Lead[]>([]);
  const [modals, setModals] = useState<{
    import: boolean;
    templates: boolean;
  }>({ import: false, templates: false });

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadLeads = useCallback(async () => {
    try {
      const data = (await apiGet('/api/leads?limit=5000')) as { leads?: Lead[] };
      const loaded = Array.isArray(data.leads) ? data.leads : [];
      setLeads(loaded);
      setStatus((prev) => ({ ...prev, error: null }));
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Error al cargar biblioteca',
      }));
    } finally {
      setStatus((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadLeads();
    refreshTimerRef.current = setInterval(loadLeads, 120000);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [loadLeads]);

  useEffect(() => {
    if (!status.message) return;
    const t = setTimeout(() => setStatus((prev) => ({ ...prev, message: null })), 3000);
    return () => clearTimeout(t);
  }, [status.message]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    let out = leads.filter((l) => {
      if (q) {
        const hay = `${l.companyName || ''} ${l.contactName || ''} ${l.contactEmail || ''} ${l.sector || ''}`;
        if (!hay.toLowerCase().includes(q)) return false;
      }
      if (stageFilter && l.stage !== stageFilter) return false;
      if (tierFilter && l.tier !== tierFilter) return false;
      return true;
    });
    out.sort((a, b) => {
      const order: Record<string, number> = { T1: 0, T2: 1, T3: 2 };
      const da = order[a.tier ?? ''] ?? 3;
      const db = order[b.tier ?? ''] ?? 3;
      if (da !== db) return da - db;
      const ua = a.updatedAt ? a.updatedAt.getTime() : 0;
      const ub = b.updatedAt ? b.updatedAt.getTime() : 0;
      return ub - ua;
    });
    return out;
  }, [leads, searchQuery, stageFilter, tierFilter]);

  const stats = useMemo(() => {
    return {
      total: leads.length,
      enriched: leads.filter((l) => l.apifyEnrichedAt).length,
      priority: leads.filter((l) => l.tier === 'T1').length,
    };
  }, [leads]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  return (
    <section className={styles.libShell} aria-label="Biblioteca de leads">
      <header className={styles.libMasthead}>
        <div className={styles.libMastheadInner}>
          <p className={styles.libKicker}>Biblioteca</p>
          <h1 className={styles.libHead}>Catálogo de leads</h1>
          <p className={styles.libDeck}>
            Inventario completo. Filtra por stage, tier o campo faltante. Cada fila abre el expediente.
          </p>
        </div>
        <div className={styles.libActions}>
          <button
            type="button"
            className={styles.libBtn}
            onClick={() => setModals((prev) => ({ ...prev, import: true }))}
          >
            Importar leads
          </button>
          <button
            type="button"
            className={`${styles.libBtn} ${styles.libBtnGhost}`}
            onClick={() => setModals((prev) => ({ ...prev, templates: true }))}
          >
            Plantillas
          </button>
        </div>
      </header>

      {status.message && (
        <div
          className={
            status.message.type === 'success'
              ? styles.toastSuccess
              : styles.toastError
          }
        >
          {status.message.text}
        </div>
      )}

      <LibraryStats loading={status.loading} stats={stats} />

      <LibraryToolbar
        searchQuery={searchQuery}
        stageFilter={stageFilter}
        tierFilter={tierFilter}
        filteredCount={filtered.length}
        totalCount={leads.length}
        onSearchChange={(v) => updateParam('q', v)}
        onStageChange={(v) => updateParam('stage', v)}
        onTierChange={(v) => updateParam('tier', v)}
      />

      <div className={styles.libRows} role="list" aria-label="Lista de leads">
        {status.loading && leads.length === 0 ? (
          <div className={styles.libEmpty}>Cargando biblioteca…</div>
        ) : status.error && leads.length === 0 ? (
          <div className={styles.libEmpty}>{status.error}</div>
        ) : filtered.length === 0 ? (
          <div className={styles.libEmpty}>Ningún lead coincide con los filtros.</div>
        ) : (
          filtered.map((l, i) => <LibraryRow key={l.id} lead={l} idx={i} />)
        )}
      </div>

      {modals.import && (
        <ImportModal
          onClose={() => setModals((prev) => ({ ...prev, import: false }))}
          onMessage={(text, type) =>
            setStatus((prev) => ({ ...prev, message: { text, type } }))
          }
          onImportSuccess={loadLeads}
        />
      )}

      {modals.templates && <TemplatesModal onClose={() => setModals((prev) => ({ ...prev, templates: false }))} />}
    </section>
  );
}

function LibraryStats({
  loading,
  stats,
}: {
  loading: boolean;
  stats: { total: number; enriched: number; priority: number };
}) {
  return (
    <div className={styles.libStats}>
      <div className={styles.libStat}>
        <div className={styles.libStatNum}>{loading ? '—' : stats.total.toLocaleString('es-ES')}</div>
        <div className={styles.libStatLabel}>Total en biblioteca</div>
      </div>
      <div className={styles.libStat}>
        <div className={styles.libStatNum}>{loading ? '—' : stats.enriched.toLocaleString('es-ES')}</div>
        <div className={styles.libStatLabel}>Con enriquecimiento Apify</div>
      </div>
      <div className={styles.libStat}>
        <div className={styles.libStatNum}>{loading ? '—' : stats.priority.toLocaleString('es-ES')}</div>
        <div className={styles.libStatLabel}>Tier 1 · prioritarios</div>
      </div>
    </div>
  );
}

function LibraryToolbar({
  searchQuery,
  stageFilter,
  tierFilter,
  filteredCount,
  totalCount,
  onSearchChange,
  onStageChange,
  onTierChange,
}: {
  searchQuery: string;
  stageFilter: string;
  tierFilter: string;
  filteredCount: number;
  totalCount: number;
  onSearchChange: (v: string) => void;
  onStageChange: (v: string) => void;
  onTierChange: (v: string) => void;
}) {
  return (
    <div className={styles.libToolbar}>
      <div className={styles.libSearchWrap}>
        <span className={styles.libSearchGlyph} aria-hidden="true">
          ⦿
        </span>
        <input
          type="search"
          className={styles.libSearch}
          placeholder="Buscar empresa, contacto, sector…"
          autoComplete="off"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <label className={styles.libFilter}>
        <span>Stage</span>
        <select value={stageFilter} onChange={(e) => onStageChange(e.target.value)}>
          <option value="">Todos</option>
          <option value="new">Nuevo</option>
          <option value="researching">Investigando</option>
          <option value="outreach">Outreach</option>
          <option value="conversation">Conversación</option>
          <option value="diagnostic">Diagnóstico</option>
          <option value="proposal">Propuesta</option>
          <option value="negotiation">Negociación</option>
          <option value="won">Ganado</option>
          <option value="lost">Perdido</option>
          <option value="dormant">Inactivo</option>
        </select>
      </label>
      <label className={styles.libFilter}>
        <span>Tier</span>
        <select value={tierFilter} onChange={(e) => onTierChange(e.target.value)}>
          <option value="">Todos</option>
          <option value="T1">T1</option>
          <option value="T2">T2</option>
          <option value="T3">T3</option>
        </select>
      </label>
      <span className={styles.libCount}>
        {filteredCount} de {totalCount}
      </span>
    </div>
  );
}

function LibraryRow({ lead, idx }: { lead: Lead; idx: number }) {
  const sector = lead.sector || '';
  const region = lead.region || '';
  const when = lead.updatedAt ? timeAgo(lead.updatedAt) : '';
  const meta: { key: string; node: React.ReactNode }[] = [];
  if (sector) meta.push({ key: 'sector', node: <span>{sector}</span> });
  if (region) meta.push({ key: 'region', node: <span>{region}</span> });
  if (when) meta.push({ key: 'when', node: <span>{when}</span> });

  const contact = lead.contactName || lead.contactEmail || '';

  return (
    <div className={styles.libRow} role="listitem">
      <span className={styles.libRowIndex}>{pad2(idx + 1)}</span>
      <div className={styles.libRowCore}>
        <div className={styles.libRowName}>
          <Link to={`/leads/${lead.id}`}>{lead.companyName || 'Sin nombre'}</Link>
        </div>
        {meta.length > 0 && (
          <div className={styles.libRowMeta}>
            {meta.map((item, i) => (
              <span key={item.key}>
                {i > 0 && <span className={styles.sep}>·</span>}
                {item.node}
              </span>
            ))}
          </div>
        )}
      </div>
      {contact ? (
        <div className={styles.libRowContact}>{contact}</div>
      ) : (
        <div className={`${styles.libRowContact} ${styles.libRowContactEmpty}`}>Sin contacto</div>
      )}
      <div className={styles.libRowStage}>{stageLabel(lead.stage)}</div>
      {lead.tier ? (
        <div className={styles.libRowTier}>
          <span className={styles.libRowTierNum}>{lead.tier.replace('T', '')}</span>
          <span className={styles.libRowTierLabel}>Tier</span>
        </div>
      ) : (
        <div className={`${styles.libRowTier} ${styles.libRowTierEmpty}`}>
          <span className={styles.libRowTierNum}>-</span>
          <span className={styles.libRowTierLabel}>Tier</span>
        </div>
      )}
      <Link to={`/leads/${lead.id}`} className={styles.libRowEdit} title="Editar">
        ✎
      </Link>
    </div>
  );
}

function ImportModal({
  onClose,
  onMessage,
  onImportSuccess,
}: {
  onClose: () => void;
  onMessage: (text: string, type: 'success' | 'error') => void;
  onImportSuccess: () => void;
}) {
  const [url, setUrl] = useState('');
  const [data, setData] = useState('');
  const [importing, setImporting] = useState(false);

  async function handleImport() {
    setImporting(true);
    try {
      let payload: unknown;
      if (data.trim()) {
        payload = JSON.parse(data.trim());
      } else if (url.trim()) {
        const r = await fetch(url.trim());
        payload = await r.json();
      } else {
        onMessage('Introduce JSON o una URL', 'error');
        setImporting(false);
        return;
      }
      if (!Array.isArray(payload)) {
        onMessage('Se espera un array de leads', 'error');
        setImporting(false);
        return;
      }
      await apiPost('/api/leads/import', { leads: payload });
      onMessage(
        `Importados ${payload.length} lead${payload.length === 1 ? '' : 's'}`,
        'success'
      );
      onClose();
      setUrl('');
      setData('');
      onImportSuccess();
    } catch (err) {
      onMessage(err instanceof Error ? err.message : 'Error al importar', 'error');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div
      className={styles.libModalOverlay}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className={styles.libModal}>
        <p className={styles.libModalEyebrow}>Import</p>
        <h2 className={styles.libModalTitle}>Importar leads</h2>
        <p className={styles.libModalNote}>
          Pega un JSON con un array de leads, o indica la URL de un CSV/JSON remoto.
        </p>
        <div className={styles.libModalField}>
          <label htmlFor="lib-import-url">URL remota (opcional)</label>
          <input
            type="url"
            id="lib-import-url"
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
        <div className={styles.libModalField}>
          <label htmlFor="lib-import-data">O pega JSON aquí</label>
          <textarea
            id="lib-import-data"
            rows={8}
            placeholder='[{"companyName":"Acme"}]'
            value={data}
            onChange={(e) => setData(e.target.value)}
          />
        </div>
        <div className={styles.libModalActions}>
          <button
            type="button"
            className={`${styles.libBtn} ${styles.libBtnGhost}`}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.libBtn}
            onClick={handleImport}
            disabled={importing}
          >
            {importing ? 'Importando…' : 'Importar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplatesModal({ onClose }: { onClose: () => void }) {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const templatePreview = useMemo(() => {
    const previews: Record<string, string> = {
      'cold-email':
        'Hola [Contacto],\n\nVi que [Empresa] trabaja en [Sector]. Nosotros producimos...',
      'warm-intro':
        'Hola [Contacto],\n\n[Referido] me sugirió que te contactara. Especialmente tras ver [señal]...',
      'follow-up':
        'Hola [Contacto],\n\nGracias por la reunión de [fecha]. Adjunto el resumen de...',
    };
    return previews[selectedTemplate] || '';
  }, [selectedTemplate]);

  return (
    <div
      className={styles.libModalOverlay}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className={styles.libModal}>
        <p className={styles.libModalEyebrow}>Plantillas</p>
        <h2 className={styles.libModalTitle}>Plantillas de outreach</h2>
        <p className={styles.libModalNote}>
          Las plantillas se usan como punto de partida cuando generas outreach para un lead desde Operar.
        </p>
        <div className={styles.libModalField}>
          <label htmlFor="lib-tpl-select">Plantilla</label>
          <select
            id="lib-tpl-select"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
          >
            <option value="">- Selecciona -</option>
            <option value="cold-email">Primer contacto en frío</option>
            <option value="warm-intro">Presentación con referido</option>
            <option value="follow-up">Follow-up tras reunión</option>
          </select>
        </div>
        <div className={styles.libModalField}>
          <label htmlFor="lib-tpl-preview">Vista previa</label>
          <textarea
            id="lib-tpl-preview"
            rows={8}
            readOnly
            placeholder="Selecciona una plantilla…"
            value={templatePreview}
          />
        </div>
        <div className={styles.libModalActions}>
          <button
            type="button"
            className={`${styles.libBtn} ${styles.libBtnGhost}`}
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
